import Icon, { IconType } from 'components/atoms/Icon';
import { FieldArray, useFormikContext } from 'formik';
import { useLocation } from 'react-router-dom';
import { SoftSkillRelationSelectFormField } from 'views/StaticContent/SoftSkills/drawers/children/SoftSkillRelationSelectFormField';
import {
  getRelationValues,
  TEditSoftSkillFormValues,
} from 'views/StaticContent/SoftSkills/drawers/SoftSkillDrawer.helpers';
import { QueryParams } from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import { Button } from '@spotted-zebra-uk/ui-components';
import styles from './SoftSkillRelations.module.scss';
import SoftSkillTabSection from './SoftSkillTabSection';

type TSoftSkillRelationsFormProps = {
  isBroadSoftSkill: boolean;
  isEditMode: boolean;
  relationType: 'vertical' | 'horizontal';
};

const pageContentCopy = {
  parent: {
    title: 'Children',
    description: (name: string) => (
      <span>
        Focused elements that belong to{' '}
        <span className={styles.skillName}>{name}</span>
      </span>
    ),
  },
  child: {
    title: 'Parents',
    description: (name: string) => (
      <span>
        Broad elements <span className={styles.skillName}>{name}</span> belongs
        to.
      </span>
    ),
  },
  sibling: {
    title: 'Siblings',
    description: (name: string) => (
      <span>
        Elements considered equivalent to{' '}
        <span className={styles.skillName}>{name}</span>.
      </span>
    ),
  },
};

const siblingsGuidanceCopy = [
  {
    title: 'Values based Right for us models',
    description: 'All sibling elements will be excluded from AI selection.',
    icon: 'favourite',
  },
  {
    title: 'Behaviour based Right for us models',
    list: [
      'If the Right for us included in the Role Skills Profile, its sibling elements will be excluded from AI selection.',
      'If the Right for us is not included, its sibling elements will remain available for AI selection.',
    ],
    icon: 'group',
  },
];

export const SoftSkillRelationsForm = ({
  isBroadSoftSkill,
  isEditMode,
  relationType,
}: TSoftSkillRelationsFormProps) => {
  const location = useLocation();
  const { values } = useFormikContext<TEditSoftSkillFormValues>();

  const pageContent =
    relationType === 'horizontal'
      ? pageContentCopy.sibling
      : pageContentCopy[isBroadSoftSkill ? 'parent' : 'child'];

  const getRelationTypeLabel = (): string => {
    if (relationType === 'horizontal') {
      return isBroadSoftSkill ? 'Broad skill' : 'Focused skill';
    }
    return isBroadSoftSkill ? 'Focused skill' : 'Broad skill';
  };

  const getRelationTypeId = (index: number): string => {
    if (relationType === 'horizontal') {
      return `similarSoftSkills.${index}`;
    }
    return isBroadSoftSkill
      ? `focusedSoftSkills.${index}`
      : `broadSoftSkills.${index}`;
  };

  const getFieldArrayName = (): string => {
    if (relationType === 'horizontal') {
      return 'similarSoftSkills';
    }
    return isBroadSoftSkill ? 'focusedSoftSkills' : 'broadSoftSkills';
  };

  const openSoftSkillInNewTab = (softSkillSubId: string | undefined) => {
    if (!softSkillSubId) return;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(QueryParams.EDIT_DRAWER, softSkillSubId);

    const newUrl = `${window.location.origin}${
      location.pathname
    }?${searchParams.toString()}`;

    window.open(newUrl, '_blank');
  };

  return (
    <SoftSkillTabSection
      title={pageContent.title}
      description={pageContent.description(values.name)}
      hasNoItems={
        getRelationValues(values, relationType, isBroadSoftSkill).length ===
          0 && !isEditMode
      }
      noItemLabel={pageContent.title.toLowerCase()}
    >
      <div className={styles.relationsContentContainer}>
        {isEditMode ? (
          <FieldArray
            name={getFieldArrayName()}
            render={fieldArrayHelpers => (
              <div className={styles.softSkillRelationsFieldsContainer}>
                {getRelationValues(values, relationType, isBroadSoftSkill).map(
                  (_, index) => (
                    <div
                      key={index}
                      className={styles.softSkillRelationContainer}
                    >
                      <SoftSkillRelationSelectFormField
                        isEditMode={isEditMode}
                        relationType={relationType}
                        id={getRelationTypeId(index)}
                        isBroadSoftSkill={isBroadSoftSkill}
                        label={getRelationTypeLabel()}
                        placeholder="Name"
                        maxMenuHeight={240}
                        index={index}
                        onDelete={() => fieldArrayHelpers.remove(index)}
                      />
                    </div>
                  )
                )}
                <Button
                  onClick={() => {
                    fieldArrayHelpers.push({ label: '', value: '' });
                  }}
                  size="medium"
                  variant="secondary"
                  type="button"
                  leftIcon={<Icon icon="add" className={styles.icon} />}
                >
                  Add
                </Button>
              </div>
            )}
          ></FieldArray>
        ) : (
          <ul className={styles.softSkillRelationsList}>
            {getRelationValues(values, relationType, isBroadSoftSkill).map(
              (value, index) => (
                <li key={index} className={styles.softSkillRelationListItem}>
                  {value.label}{' '}
                  <Button
                    size="medium"
                    variant="tertiary"
                    leftIcon={<Icon icon="openTab" />}
                    className={styles.openSiblingTabIcon}
                    onClick={() => openSoftSkillInNewTab(value.value)}
                  />
                </li>
              )
            )}
          </ul>
        )}
        {relationType === 'horizontal' && (
          <div className={styles.siblingsGuidance}>
            <h4 className={styles.title}>
              How siblings affect Right for us AI skill selection
            </h4>
            {siblingsGuidanceCopy.map((item, index) => (
              <div key={index} className={styles.siblingsGuidanceItem}>
                <div className={styles.siblingsGuidanceItemIcon}>
                  <Icon icon={item.icon as IconType} />
                </div>
                <div className={styles.siblingsGuidanceItemContent}>
                  <p className={styles.siblingsGuidanceItemTitle}>
                    {item.title}
                  </p>
                  {item.list && (
                    <ul className={styles.siblingsGuidanceItemList}>
                      {item.list.map((description, index) => (
                        <li
                          key={index}
                          className={styles.siblingsGuidanceItemListDescription}
                        >
                          {description}
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.description && (
                    <p className={styles.siblingsGuidanceItemDescription}>
                      {item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SoftSkillTabSection>
  );
};
